/**
 * live-texture.js — עדכון פני הכרית בלי לבנות אותה מחדש.
 * ⚠️ קוד פיתוח בלבד. לא נוגע ב-Shopify ולא באתר החי.
 *
 * ═══ מה זה פותר ═══
 *
 * generate3D עושה שני דברים שכרוכים היום יחד:
 *   1. גאומטריה — מעקב קונטור, convex hull, בניית mesh. ~2,200ms.
 *   2. טקסטורה  — gl.texImage2D אחד. ~1ms.
 *
 * כל עוד הסילואט לא משתנה, הגאומטריה תקפה. הזזת התמונה בתוך החור של
 * תבנית, הסרת רקע, שינוי כיתוב — אף אחד מהם לא נוגע בצורה, ולכן אין
 * שום סיבה לשלם את ה-2,200ms.
 *
 * ═══ המכשול, ולמה הפתרון נראה כך ═══
 *
 * הטקסטורה הקדמית שמורה ב-custom-script.js כ-`let texture` ברמת
 * הסקריפט. `let` לא יוצר תכונה על window, ולכן היא לא נגישה מבחוץ.
 * drawScene קושר אותה ומצייר. אם נכתוב פיקסלים חדשים *לתוך אותו
 * אובייקט טקסטורה*, drawScene יציג אותם מיד.
 *
 * צריך רק את המצביע. משיגים אותו בלי לגעת במנוע: עוטפים את
 * gl.bindTexture ו-gl.texImage2D, ורואים אילו טקסטורות המנוע מעלה
 * אליהן קנבס.
 *
 * ═══ למה לפי "קנבס" ולא לפי סדר יצירה ═══
 *
 * אפשר היה לתפוס לפי הסדר שבו gl.createTexture נקרא, אבל זה נשען על
 * סדר פנימי שרירותי. הזיהוי כאן נשען על *מה שהמנוע באמת עושה*:
 * הטקסטורות של הכרית הן היחידות שמקבלות HTMLCanvasElement.
 * תווית המותג מגיעה מ-Image, וטקסטורת הצבע היא Uint8Array 1×1.
 * הראשונה כרונולוגית היא הקדמית, השנייה היא הגב.
 *
 * ═══ הגבול ═══
 *
 * זה תקף *רק* כשהסילואט לא משתנה. במצב "לפי התמונה" הסילואט הוא
 * התמונה, וכל הזזה משנה את הצורה — שם חייבים generate3D מלא.
 * הקורא אחראי לדעת באיזה מצב הוא נמצא. isReady() לא יודע את זה.
 *
 * ═══ שבירות ידועה ═══
 *
 * זהו "מסלול A" מתוך architecture/dynamic-preview-research.md: אפס
 * שינוי ב-custom-script.js, ולכן אפס סיכון לאתר החי — אבל הוא נשען
 * על התנהגות פנימית של המנוע. שינוי עתידי באופן שבו הוא מעלה
 * טקסטורות ישבור את זה *בשקט*. לכן:
 *   • verify() בודקת שהתפיסה הצליחה ומחזירה false אם לא.
 *   • כל קורא חייב ליפול חזרה ל-generate3D כשמתקבל false.
 * לפני העלאה לייב יש להחליף למסלול B (14 שורות תוספתיות במנוע).
 */
(function (global) {
  'use strict';

  let installed = false;
  let capturing = false;
  let boundTex = null;
  let glRef = null;
  let captured = [];
  // ═══ הגב (אלעד, 12.9) ═══
  // המנוע מעלה את טקסטורת הגב שלו (עותק של החזית) *אחרי* שהגאומטריה מוכנה,
  // ב-requestIdleCallback — כלומר בזמן שאינו ידוע מראש, לפעמים אחרי שהקורא
  // כבר דחף גב משלו. כדי שהגב שלנו (כיתוב משוקף) ינצח תמיד, הקורא נרשם כאן
  // ומקבל קריאה מיד אחרי שהמנוע העלה את הגב שלו.
  let backCb = null;
  let backUploads = 0;   // כמה פעמים המנוע העלה גב מאז beginCapture (לבדיקות)
  // ההעלאות של update() עצמו עוברות באותו texImage2D (דרך updatePillowTexture
  // של המנוע). הדגל מבדיל אותן מההעלאות של המנוע: המנוע מעלה בדיוק שני קנבסים
  // בכל בנייה — חזית ואז גב (custom-script.js שורות 1100, 1070) — ושום קנבס אחר.
  let selfUploading = false;

  function install() {
    if (installed) return;
    installed = true;

    const ctors = [global.WebGLRenderingContext, global.WebGL2RenderingContext].filter(Boolean);
    for (const C of ctors) {
      const proto = C.prototype;

      const origBind = proto.bindTexture;
      proto.bindTexture = function (target, tex) {
        if (target === this.TEXTURE_2D) { boundTex = tex; glRef = this; }
        return origBind.apply(this, arguments);
      };

      const origTexImage = proto.texImage2D;
      proto.texImage2D = function () {
        let engineBack = false;
        // התפיסה נמשכת עד שהמנוע העלה את שני הקנבסים שלו (או עד beginCapture הבא),
        // גם אם update() כבר נקרא בינתיים — אחרת הגב של המנוע היה חומק מאיתנו
        // כשהוא מגיע אחרי הדחיפה הראשונה שלנו (קרה בדסקטופ).
        if (capturing && !selfUploading && boundTex) {
          const src = arguments[arguments.length - 1];
          if (src && global.HTMLCanvasElement && src instanceof global.HTMLCanvasElement) {
            if (captured.indexOf(boundTex) === -1) captured.push(boundTex);
            // הטקסטורה השנייה כרונולוגית היא הגב של המנוע.
            engineBack = captured.length >= 2 && boundTex === captured[1];
            if (captured.length >= 2) capturing = false;
          }
        }
        const r = origTexImage.apply(this, arguments);
        if (engineBack) {
          backUploads++;
          // אחרי שהמנוע סיים (onComplete שלו מצייר סינכרונית מיד אחרי ההעלאה).
          if (backCb) setTimeout(backCb, 0);
        }
        return r;
      };
    }
  }

  /** לקרוא מיד לפני כל generate3D. */
  function beginCapture() {
    install();
    captured = [];
    backUploads = 0;
    capturing = true;
  }

  /** מי לקרוא אחרי שהמנוע העלה את הגב שלו (בכל בנייה). null מבטל. */
  function onEngineBack(cb) { backCb = typeof cb === 'function' ? cb : null; }

  /**
   * האם אפשר לעדכן חי.
   * מסלול B (מומלץ): המנוע חושף updatePillowTexture — אין תלות בתפיסה.
   * מסלול A (fallback): תפסנו לפחות טקסטורה אחת + drawScene קיים.
   */
  function hasEnginePath() {
    return typeof global.updatePillowTexture === 'function';
  }
  function isReady() {
    return hasEnginePath() ||
      !!(glRef && captured.length >= 1 && typeof global.drawScene === 'function');
  }

  /**
   * החלפת פני הכרית. frontCanvas הוא ה-composite של החזית; backCanvas הוא
   * מה שיודפס על הגב — באותה מערכת קואורדינטות של החזית (לא במראה!).
   *
   * ═══ למה לא מראה (אלעד, 12.9) ═══
   * הגאומטריה של הגב היא המראה של החזית, והיא מציגה את הטקסטורה במראה.
   * עד עכשיו נדחף כאן mirror(front): התמונה יצאה ישרה אבל לא התיישרה עם קו
   * המתאר — והראש נחתך בצד (בדיוק ההערה ב-custom-script על "עותק, לא שיקוף").
   * עכשיו הגב = עותק של החזית, ורק אותיות הכיתוב משוקפות (app.js → genPair).
   * בלי backCanvas — הגב הוא עותק של החזית, כמו שהמנוע עצמו עושה.
   *
   * מחזיר false אם העדכון לא הצליח — אז על הקורא לבנות מחדש.
   *
   * ═══ סדר המסלולים ═══
   * 1. מסלול B — window.updatePillowTexture של המנוע. יציב, לא נשען
   *    על סדר פנימי. זה המסלול המועדף אחרי שהתוספת נכנסה למנוע.
   * 2. מסלול A — הטקסטורות שנתפסו בעטיפת texImage2D. נשאר כרשת
   *    ביטחון למקרה שהמנוע נטען בלי התוספת (למשל גרסה ישנה בקאש).
   * בשני המקרים כישלון מחזיר false והקורא נופל ל-generate3D.
   */
  function update(frontCanvas, backCanvas) {
    const back = backCanvas || frontCanvas;
    // (12.9) update() כבר לא מכבה את התפיסה — ההעלאות שלנו מסומנות ב-selfUploading
    // ולכן לא נספרות כהעלאות של המנוע, והתפיסה נגמרת מעצמה אחרי הגב של המנוע.
    selfUploading = true;
    try {
      // מסלול B — הדרך היציבה.
      if (hasEnginePath()) {
        try {
          const ok = global.updatePillowTexture(frontCanvas, back);
          if (ok) return true;
          // false מהמנוע = אין מודל עדיין; ננסה מסלול A אם יש, אחרת ניפול.
        } catch (err) {
          console.warn('updatePillowTexture נכשל, מנסים מסלול A:', err);
        }
      }
      // מסלול A — רשת ביטחון.
      if (!(glRef && captured.length >= 1 && typeof global.drawScene === 'function')) return false;
      return updateCaptured(frontCanvas, back);
    } finally {
      selfUploading = false;
    }
  }

  function updateCaptured(frontCanvas, back) {
    const gl = glRef;
    try {
      gl.bindTexture(gl.TEXTURE_2D, captured[0]);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, frontCanvas);
      setParams(gl);

      if (captured[1]) {
        gl.bindTexture(gl.TEXTURE_2D, captured[1]);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, back);
        setParams(gl);
      }
      global.drawScene();
      return true;
    } catch (err) {
      console.warn('עדכון טקסטורה נכשל, נדרשת בנייה מחדש:', err);
      return false;
    }
  }

  function setParams(gl) {
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  }

  // mirror() הוסר (12.9): שיקוף הגב הוא מה שחתך את הראש. ראו ההערה ב-update.

  function stats() { return { captured: captured.length, hasGl: !!glRef, backUploads }; }

  global.PZLiveTexture = { install, beginCapture, onEngineBack, isReady, update, stats };
  install();
})(window);
